'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Topbar from './Topbar';
import BottomTabBar from './BottomTabBar';
import OfflineSyncBanner from './OfflineSyncBanner';
import OnboardingTour from './OnboardingTour';
import { SkeletonScreen } from './ui/Skeleton';
import { useAuth } from '@/contexts/AuthContext';


interface AppLayoutProps {
  children: React.ReactNode;
  activeTab?: 'dashboard' | 'certs' | 'cv' | 'vault' | 'profile';
}

export default function AppLayout({ children, activeTab }: AppLayoutProps) {
  const router = useRouter();
  const { user, profile, loading, isConfigured } = useAuth();
  const isAuthenticated = isConfigured ? !!user : !!profile;

  // Android hardware back button handling.
  // Capacitor fires a synthetic 'backbutton' DOM event. If we call preventDefault(),
  // Capacitor cancels the native back action. If we don't, Capacitor's default runs
  // (finish the Activity = minimize/exit). We only preventDefault when a PushedOverlay
  // is open so we can dismiss it instead of exiting.
  useEffect(() => {
    const handleBackButton = (e: Event) => {
      if (window.history.state?.pushedOverlay) {
        // Overlay is open — intercept and pop it.
        e.preventDefault();
        window.history.back();
      }
      // At root: do NOT preventDefault → Capacitor's native default exits the app.
    };
    document.addEventListener('backbutton', handleBackButton, false);
    return () => document.removeEventListener('backbutton', handleBackButton, false);
  }, []);

  const blurFocusedField = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) {
      active.blur();
    }
  };

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.replace('/sign-up-login-screen');
    }
  }, [isAuthenticated, loading, router]);

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen app-modern-shell flex flex-col">
        <div className="flex-1 overflow-hidden">
          <SkeletonScreen />
        </div>
        {/* Placeholder bottom nav so layout doesn't jump */}
        <div className="bottom-tab-bar md:hidden" aria-hidden="true" style={{ height: 'calc(56px + env(safe-area-inset-bottom))' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen app-modern-shell flex flex-col relative overflow-hidden" onPointerDownCapture={blurFocusedField}>
      <div className="app-bg-grid" aria-hidden="true" />
      <div className="app-bg-sea" aria-hidden="true" />
      {/* Global app bar: desktop/tablet only. On mobile each screen owns its header per the Seamigo design. */}
      <div className="hidden md:block">
        <Topbar />
      </div>
      <OfflineSyncBanner />
      <main className="app-main app-main--safe relative z-10 flex-1 pb-20 md:pb-8 max-w-screen-2xl mx-auto w-full px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-16 pt-4">
        {children}
      </main>
      <BottomTabBar activeTab={activeTab} />
      <OnboardingTour />
    </div>
  );
}
